import { Path } from "@kform/core";
import * as React from "react";

import { InvalidPathError } from "../utils/errors";
import {
  formatListableAsArray,
  Listable,
  parseArrayAsListable,
} from "../utils/listableUtils";
import { useLatestValues } from "../utils/useLatestValues";
import { Controller } from "./useController";
import { FormatterControllerState } from "./useFormatter";
import { InputOptions, InputOwnController, useInput } from "./useInput";

/**
 * Options available to the {@link useListableInput} hook.
 */
export type ListableInputOptions<
  T = unknown,
  TListable extends Listable<T> | null = Listable<T> | null,
  TFormatted = T[] | null,
  TInput = HTMLInputElement,
> = Omit<InputOptions<TListable, TFormatted, TInput>, "format" | "parse"> &
  ListableInputOwnOptions<T, TListable, TFormatted, TInput>;

/**
 * Own options available to the {@link useListableInput} hook.
 */
export interface ListableInputOwnOptions<
  T = unknown,
  TListable extends Listable<T> | null = Listable<T> | null,
  TFormatted = T[] | null,
  TInput = HTMLInputElement,
> {
  formatFromArray?: (
    arrayValue: T[] | null,
    state: FormatterControllerState<TListable, TFormatted>,
    input: TInput | null,
  ) => TFormatted;
  parseToArray?: (
    formattedValue: TFormatted,
    state: FormatterControllerState<TListable, TFormatted>,
    input: TInput | null,
  ) => T[] | null;
}

/**
 * Controller used to read and control the form value associated with the form
 * input, exposing properties that should be set on the input itself.
 */
export type ListableInputController<
  T = unknown,
  TListable extends Listable<T> | null = Listable<T> | null,
  TFormatted = T[] | null,
  TInput = HTMLInputElement,
> = Controller<TListable> & ListableInputOwnController<T, TFormatted, TInput>;

/**
 * Own listable input controller.
 */
export type ListableInputOwnController<
  T = unknown,
  TFormatted = T[],
  TInput = HTMLInputElement,
> = InputOwnController<TFormatted, TInput>;

/**
 * Allowed listable types.
 */
const ALLOWED_COLLECTION_TYPES = ["Array", "List", "Table"];

/**
 * Hook controlling the integration between a listable value (`Array`, `List`,
 * or `Table`) of the form manager and an input of the form.
 * @param path Path of the listable form value to access, relative to the
 * current path.
 * @param options Available options.
 * @throws {InvalidPathError} When the schema at {@link path} is not listable
 * (not `Array`, `List`, or `Table`).
 * @returns A controller used to read and control the listable form value
 * associated with the form input, exposing properties that should be set on the
 * input itself.
 */
export function useListableInput<
  T = unknown,
  TListable extends Listable<T> | null = Listable<T> | null,
  TFormatted = T[] | null,
  TInput = HTMLInputElement,
>(
  path?: Path | string,
  options?: undefined,
): ListableInputController<T, TListable, TFormatted, TInput>;
export function useListableInput<
  T = unknown,
  TListable extends Listable<T> | null = Listable<T> | null,
  TFormatted = T[] | null,
  TInput = HTMLInputElement,
>(
  path: Path | string | undefined,
  options: ListableInputOptions<T, TListable, TFormatted, TInput>,
): ListableInputController<T, TListable, TFormatted, TInput>;
export function useListableInput<
  T = unknown,
  TListable extends Listable<T> | null = Listable<T> | null,
  TFormatted = T[] | null,
  TInput = HTMLInputElement,
>(
  path: Path | string = Path.CURRENT,
  {
    formatFromArray,
    parseToArray,
    ...options
  }: ListableInputOptions<T, TListable, TFormatted, TInput> = {},
): ListableInputController<T, TListable, TFormatted, TInput> {
  const latestOptions = useLatestValues({ parseToArray });

  // Input options
  const format = React.useCallback(
    (
      value: TListable | undefined,
      state: FormatterControllerState<TListable, TFormatted>,
      input: TInput | null,
    ): TFormatted => {
      const array = formatListableAsArray<T, TListable>(value, state.schema);
      return formatFromArray
        ? formatFromArray(array, state, input)
        : (array as TFormatted);
    },
    [formatFromArray],
  );
  const parse = React.useCallback(
    (
      formattedValue: TFormatted,
      state: FormatterControllerState<TListable, TFormatted>,
      input: TInput | null,
    ): TListable => {
      return parseArrayAsListable(
        latestOptions.parseToArray
          ? latestOptions.parseToArray(formattedValue, state, input)
          : (formattedValue as T[]),
        state.schema,
      );
    },
    [latestOptions],
  );

  const inputController = useInput(path, { format, parse, ...options });

  // Validate schema type
  const { typeInfo } = inputController.useSchema();
  if (!ALLOWED_COLLECTION_TYPES.includes(typeInfo.name)) {
    throw new InvalidPathError(
      inputController.getState().path,
      "Unsupported schema: Expected schema representing (possibly nullable) " +
        "listable values, but got schema representing values of type " +
        `'${typeInfo.toString()}'.`,
    );
  }

  return inputController;
}
